import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AtSign, Bell, Calendar, CheckCheck, MessageSquare, ShoppingBag, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

interface Notification {
  id: string;
  title: string;
  message: string | null;
  type: string;
  is_read: boolean;
  created_at: string;
  related_entity_id: string | null;
  related_entity_type: string | null;
}

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("dashboard_notifications")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_read", false)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data as Notification[];
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("dashboard_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("dashboard_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("user_id", user.id)
        .eq("is_read", false);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const CREATIVE_TRAVEL_BRAND_ID = "ef0ce1ff-c11a-4641-9fa4-e6d9c2883670";

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }

    // Navigate to related entity based on type
    if (notification.related_entity_id && notification.related_entity_type) {
      switch (notification.related_entity_type) {
        case "calendar_event":
          navigate(`/calendar?eventId=${notification.related_entity_id}`);
          break;
        case "lead":
          navigate(/intake/i.test(notification.title || "")
            ? `/lead/${notification.related_entity_id}?tab=student-intake`
            : `/lead/${notification.related_entity_id}?tab=communications`);
          break;
        case "student_intake_submission": {
          const { data: sub } = await (supabase as any)
            .from("student_intake_submissions")
            .select("lead_id")
            .eq("id", notification.related_entity_id)
            .maybeSingle();
          if (sub?.lead_id) navigate(`/lead/${sub.lead_id}?tab=student-intake&submissionId=${notification.related_entity_id}`);
          break;
        }
        case "channel_message": {
          // related_entity_id may be a message ID, so look up the actual channel_id
          const { data: msgData } = await supabase
            .from("channel_messages")
            .select("channel_id")
            .eq("id", notification.related_entity_id)
            .single();
          const resolvedChannelId = msgData?.channel_id || notification.related_entity_id;
          navigate(`/?openMessaging=true&channelId=${resolvedChannelId}`);
          break;
        }
        case "client":
          navigate(`/clients?client=${notification.related_entity_id}`);
          break;
        case "booking":
          navigate(`/operations/coffeebeans?booking=${notification.related_entity_id}`);
          break;
        case "pending_booking":
          navigate(`/operations/coffeebeans?view=recovery&checkout=${notification.related_entity_id}`);
          break;
        case "coffeebeans_booking": {
          // Look up brand_id to route to the correct operations page
          const { data: booking } = await supabase
            .from("coffeebeans_bookings")
            .select("brand_id")
            .eq("id", notification.related_entity_id)
            .single();

          if (booking?.brand_id === CREATIVE_TRAVEL_BRAND_ID) {
            navigate(`/operations/creative-travel?booking=${notification.related_entity_id}`);
          } else {
            navigate(`/operations/coffeebeans?booking=${notification.related_entity_id}`);
          }
          break;
        }
        default:
          console.warn(`Unhandled notification type: ${notification.related_entity_type}`);
          break;
      }
    }

    setOpen(false);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "meeting_invitation":
        return <Users className="h-4 w-4 text-primary" />;
      case "calendar":
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case "mention":
        return <AtSign className="h-4 w-4 text-orange-500" />;
      case "channel_message":
        return <MessageSquare className="h-4 w-4 text-green-500" />;
      case "booking":
        return <ShoppingBag className="h-4 w-4 text-purple-500" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const groupNotificationsByDate = (notifications: Notification[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: { title: string; notifications: Notification[] }[] = [
      { title: "Today", notifications: [] },
      { title: "Yesterday", notifications: [] },
      { title: "Earlier", notifications: [] },
    ];

    notifications.forEach((notification) => {
      const notifDate = new Date(notification.created_at);
      notifDate.setHours(0, 0, 0, 0);

      if (notifDate.getTime() === today.getTime()) {
        groups[0].notifications.push(notification);
      } else if (notifDate.getTime() === yesterday.getTime()) {
        groups[1].notifications.push(notification);
      } else {
        groups[2].notifications.push(notification);
      }
    });

    return groups.filter((g) => g.notifications.length > 0);
  };

  const groupedNotifications = groupNotificationsByDate(notifications);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => markAllAsReadMutation.mutate()}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[320px]">
          {isLoading ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-muted-foreground text-sm">No notifications</p>
            </div>
          ) : (
            <div>
              {groupedNotifications.map((group, groupIndex) => (
                <div key={group.title}>
                  {groupIndex > 0 && <Separator />}
                  <div className="px-4 py-2 bg-muted/50">
                    <span className="text-xs font-medium text-muted-foreground">
                      {group.title}
                    </span>
                  </div>
                  {group.notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors border-b last:border-b-0 ${
                        !notification.is_read ? "bg-primary/5" : ""
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm ${!notification.is_read ? "font-medium" : ""}`}>
                              {notification.title}
                            </p>
                            {!notification.is_read && (
                              <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                          {notification.message && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDistanceToNow(new Date(notification.created_at), {
                              addSuffix: true,
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export { NotificationBell };
